import { useEffect, useMemo, useRef } from "react";
import useWindowDimensions from "hooks/windowDimensions";
import { useSelector } from "react-redux";
import { List as VirtualizedList } from "react-virtualized";
import { useGetSettingsQuery } from "redux/queries/settings.query";
import { flightsListSelector } from "redux/slices/flightsList.slice";

import NoMatchMessage, { NO_MATCH_MESSAGE_HEIGHT } from "../NoMatchMessage";
import FlightItem, {
  DEFAULT_FLIGHT_CARD_HEIGHT,
  VIA_FLIGHT_CARD_HEIGHT,
} from "./FlightItem";
import SmartGateBanner, {
  SMART_GATE_BANNER_HEIGHT_LARGE,
  SMART_GATE_BANNER_HEIGHT_SMALL,
} from "./SmartGateBanner";

const List = ({
  flightsToRender,
  areFlightsLoading,
  spacerHeight,
  noTerminalFlights,
  scrollPosition,
  handleScroll,
  terminal,
  noFavoriteFlights,
}) => {
  const { height, width } = useWindowDimensions();
  const { data: settingsData = {} } = useGetSettingsQuery(undefined, {
    refetchOnReconnect: true,
  });

  const listRef = useRef();
  const { lastSelectedFlightItem, smartGateBannerVisible } =
    useSelector(flightsListSelector);
  const lastScrolledAmountInList = lastSelectedFlightItem.scrolledAmountInList;

  useEffect(() => {
    if (listRef.current?.recomputeRowHeights) {
      listRef.current.recomputeRowHeights();
    }
  }, [flightsToRender]);

  useEffect(() => {
    if (
      listRef.current &&
      lastScrolledAmountInList !== null &&
      lastScrolledAmountInList > height
    ) {
      listRef.current.scrollToPosition(lastScrolledAmountInList);
    }
  }, [height, lastScrolledAmountInList]);

  const list = useMemo(
    () =>
      settingsData.featureMap?.smart_passports &&
      !areFlightsLoading &&
      !noFavoriteFlights &&
      flightsToRender.length &&
      smartGateBannerVisible
        ? [
            { flight_item_type: "SMART_GATE_BANNER" },
            ...flightsToRender.map((flight) => ({
              ...flight,
              flight_item_type: "FLIGHT",
            })),
          ]
        : flightsToRender.map((flight) => ({
            ...flight,
            flight_item_type: "FLIGHT",
          })),
    [
      settingsData.featureMap?.smart_passports,
      areFlightsLoading,
      noFavoriteFlights,
      flightsToRender,
      smartGateBannerVisible,
    ]
  );

  const getRowHeight = ({ index }) => {
    let extraHeight = 0;
    if (index === 0) {
      return spacerHeight;
    }

    const offsetIndex = index - 1;

    switch (list[offsetIndex].flight_item_type) {
      case "SMART_GATE_BANNER":
        if (width <= 396) {
          return SMART_GATE_BANNER_HEIGHT_SMALL;
        } else {
          return SMART_GATE_BANNER_HEIGHT_LARGE;
        }
      case "FLIGHT":
        if (index === list.length) {
          extraHeight = 86; //Makes room for the Actions menu
        }
        return list[offsetIndex]?.via
          ? VIA_FLIGHT_CARD_HEIGHT + extraHeight
          : DEFAULT_FLIGHT_CARD_HEIGHT + extraHeight;
      default:
        return 0;
    }
  };

  const rowRenderer = ({ key, index, style }) => {
    if (index === 0) {
      return (
        <div id="spacer" key={key} style={style}>
          {noTerminalFlights && (
            <NoMatchMessage
              verticalOffset={spacerHeight - NO_MATCH_MESSAGE_HEIGHT}
            />
          )}
        </div>
      );
    }

    const offsetIndex = index - 1;

    switch (list[offsetIndex].flight_item_type) {
      case "SMART_GATE_BANNER":
        return (
          <div key={key} style={style}>
            <SmartGateBanner />
          </div>
        );
      case "FLIGHT":
        return (
          <div key={key} style={style}>
            <FlightItem
              scrolledAmountInList={scrollPosition}
              key={list[offsetIndex].ref_id}
              flight={list[offsetIndex]}
              terminalToFilter={terminal}
              isHighlighted={
                lastSelectedFlightItem.id === list[offsetIndex].ref_id
              }
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <VirtualizedList
      className="containerList"
      ref={listRef}
      onScroll={handleScroll}
      height={height}
      width={width}
      overscanRowCount={40}
      rowRenderer={rowRenderer}
      rowHeight={getRowHeight}
      estimatedRowSize={list.length + 1} // +1 is the spacer
      rowCount={list.length + 1}
    />
  );
};

export default List;
